import React, { ChangeEvent, useMemo, useState } from "react";
import Modal from "react-modal";
import ChevronDirectionEnum from "../../../models/ChevronDirectionEnum";
import IAttachment from "../../../models/IAttachment";
import ICatalog_Item from "../../../models/ICatalog_Item";
import IOrder from "../../../models/IOrder";
import ISupplierModel from "../../../models/ISupplierModel";
import IUserUI from "../../../models/IUserUI";
import IVendorModel from "../../../models/IVendorModel";
import CatalogItemService from "../../../services/CatalogItemService";
import OrderEntryService from "../../../services/OrderEntryService";
import SupplierService from "../../../services/SupplierService";
import UIService from "../../../services/UIService";
import UserService from "../../../services/UserService";
import ChevronToggle from "../../shared/ChevronToggle/ChevronToggle";
import OwButton from "../../shared/OwButton/OwButton";
import OwInputWrapper from "../../shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "../../shared/OwLabel/OwLabel";

import "./OwCategoryCatalogItemDetailRow.scss";
import OwS3DownloadLink from "../../shared/OwS3DownloadLink/OwS3DownloadLink";
import OwS3ImageViewer from "../../shared/OwS3ImageViewer/OwS3ImageViewer";
import { PackQtyViewer } from "../../shared/PackQtyViewer/PackQtyViewer";
import { ItemInventoryByWarehouseModal } from "../ItemInventoryByWarehouseModal/ItemInventoryByWarehouseModal";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import IWarehouse from "../../../models/IWarehouse";
import { Socket } from "socket.io-client";
import { PricePerUnit } from "../../shared/PricePerUnit/PricePerUnit";
import { QtyOnHandValue } from "../../shared/QtyOnHandValue/QtyOnHandValue";
import { UomSetSelectOption } from "../../shared/UomSetSelectOption/UomSetSelectOption";
import IUnit_Of_Measurement_Set from "../../../models/IUnit_Of_Measurement_Set";
import { DescriptionExpander } from "../../shared/DescriptionExpander/DescriptionExpander";
import { FilesDownloadSection } from "../../shared/FilesDownloadSection/FilesDownloadSection";
import OwPill from "../../shared/OwPill/OwPill";
import { PriceBandPriceDisplay } from "../../shared/PriceBandPriceDisplay/PriceBandPriceDisplay";
import { IAddBulkItems } from "@models/IAddBulkItems";
import { OrderItemOptions } from "@components/shared/OrderItemOptions/OrderItemOptions";
import IOrder_Item_Option from "@models/IOrder_Item_Option";
import { ProductGroupSelector } from "@components/shared/ProductGroupSelector/ProductGroupSelector";
import { DimensionWidget } from "@components/shared/DimensionWidget/DimensionWidget";

type Props = {
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    vendorModel: IVendorModel;
    catalogItem: ICatalog_Item;
    order: IOrder;
    addToCart: (item: IAddBulkItems) => void;
    bulkAddToCart: (items: IAddBulkItems[]) => void;
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
    warehouses: IWarehouse[];
};

const OwCategoryCatalogItemDetailRow: React.FC<Props> = (props) => {
    const [detailVisible, setDetailVisible] = useState<boolean>(false);
    const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
    const [qty, setQty] = useState<number>(0);
    const [showQtyOnHand, setShowQtyOnHand] = useState<boolean>(false);
    const [existingQtyOnHand, setExistingQtyOnHand] = useState<number>(0);
    const [enableAdd, setEnableAdd] = useState<boolean>(false);
    const [modalComponent, setModalComponent] = useState<any>(null);
    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
    const [selectedUnit, setSelectedUnit] = useState<IUnit_Of_Measurement_Set | undefined>(props.catalogItem.Base_Unit);
    const [selectedItemOptions, setSelectedItemOptions] = useState<IOrder_Item_Option[]>([]);
    const [depth, setDepth] = useState<number>(0);

    const getToOrderQty = (item: ICatalog_Item, value: number): number => {
        let existingOrderQtyOnHand = 0;

        if (props.order.Order_Items) {
            for (const orderItem of props.order.Order_Items) {
                if (orderItem.Catalog_Item?.guid === item.guid) {
                    existingOrderQtyOnHand += Number(orderItem.qty);
                }
            }
        }

        const toOrderTotal = Number(value) + existingOrderQtyOnHand;

        return toOrderTotal;
    };

    const validateMinOrderQty = (item: ICatalog_Item, value: number): boolean => {
        let isQtyValid = true;

        const toOrderTotal = getToOrderQty(item, value);

        if (props.catalogItem.min_order_qty) {
            if (toOrderTotal >= props.catalogItem.min_order_qty) {
                isQtyValid = true;
            } else {
                isQtyValid = false;
            }
        }

        return isQtyValid;
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.type === "checked" ? e.target.checked : e.target.value;
        const name = e.target.name;

        const details = OrderEntryService.doInventoryValidation(
            props.catalogItem,
            Number(value),
            props.order.Order_Items,
            props.supplierModel,
            props.order,
            0,
            selectedUnit
        );

        console.log("handleChange: ", details);

        setQty(details.qty);
        setEnableAdd(details.enableAdd);
    };

    const toggleChevron = () => {
        setDetailVisible(!detailVisible);
    };

    const setSelectedItemOptionsFn = (itemOptions: IOrder_Item_Option[]) => {
        setSelectedItemOptions(itemOptions);
    };

    const getDetailTable = () => {
        let tableClass = "table-dark";

        if (UIService.isMainViewLight()) {
            tableClass = "";
        }

        const showInventoryDetails = UserService.showInventoryDetails(props.supplierModel, props.userModel);

        const deliveryEtaDetail =
            props.supplierModel.has_catalog_item_delivery_eta && props.catalogItem.delivery_eta ? (
                <tr className={"mt-2"}>
                    <th>
                        <OwLabel title={"delivery_eta"} />
                    </th>
                    <td>
                        <div>{UserService.parseDate(props.catalogItem.delivery_eta)}</div>
                    </td>
                </tr>
            ) : null;

        const priceDetail = OrderEntryService.getDiscountedPriceForCatalogItem(props.catalogItem, props.supplierModel, props.order);
        let price = priceDetail.discountedPrice;

        const hidePricing = UserService.hidePricing(props.supplierModel, props.userModel, props.vendorModel);

        let currency = props.order?.Catalog?.Currency ? props.order.Catalog.Currency : undefined;

        if (props.order?.Currency) {
            currency = props.order.Currency;
        }

        const prefix = currency ? OrderEntryService.getCurrencySymbol(currency.name) : "$";

        if (currency && props.order?.Catalog?.Currency) {
            price = OrderEntryService.convertCurrency(price, props.order?.Catalog?.Currency, currency, props.order?.exchange_rate);
        }

        const qtyOnHandMapItem = props.order.Shipping_Warehouse
            ? CatalogItemService.getQtyOnHandWarehouseMapItem(props.catalogItem, props.order.Shipping_Warehouse)
            : undefined;

        return (
            <table className={"table table-sm table-striped " + tableClass}>
                <tbody>
                    <tr>
                        <td className={"w-10r"}>
                            <OwLabel title={"name"} isBold={true} noBottomMargin={true} postText={":"} />
                        </td>
                        <td>{props.catalogItem.name}</td>
                    </tr>
                    <tr>
                        <td>
                            <OwLabel title={"sku"} isBold={true} noBottomMargin={true} postText={":"} />
                        </td>
                        <td>{props.catalogItem ? props.catalogItem.sku : null}</td>
                    </tr>
                    <tr>
                        <td>
                            <OwLabel title={"upc"} isBold={true} noBottomMargin={true} postText={":"} />
                        </td>
                        <td>{props.catalogItem ? props.catalogItem.upc : null}</td>
                    </tr>
                    <tr>
                        <td>
                            <OwLabel title={"description"} isBold={true} noBottomMargin={true} postText={":"} />
                        </td>
                        <td className={"pre-wrap"}>{props.catalogItem ? props.catalogItem.description : null}</td>
                    </tr>
                    {!hidePricing && props.supplierModel.has_map ? (
                        <tr>
                            <td>
                                <OwLabel title={"MAP"} isBold={true} noBottomMargin={true} postText={":"} />
                            </td>
                            <td>
                                <PricePerUnit
                                    priceDetail={{
                                        discountedPrice: props.catalogItem?.map || 0,
                                        originalPrice: props.catalogItem?.map || 0,
                                    }}
                                    prefix={prefix}
                                    currency={currency}
                                    baseCurrency={props.order.Catalog?.Currency}
                                    exchangeRate={props.order.exchange_rate}
                                />
                            </td>
                        </tr>
                    ) : null}
                    {hidePricing ? null : (
                        <tr>
                            <td>
                                <OwLabel title={"price_per_unit"} isBold={true} noBottomMargin={true} postText={":"} />
                            </td>
                            <td>
                                {props.catalogItem &&
                                props.catalogItem.pricing &&
                                props.order.Price_Group &&
                                !props.catalogItem.has_price_bands ? (
                                    <PricePerUnit
                                        priceDetail={priceDetail}
                                        prefix={prefix}
                                        currency={currency}
                                        baseCurrency={props.order.Catalog?.Currency}
                                        exchangeRate={props.order.exchange_rate}
                                    />
                                ) : null}

                                {props.catalogItem &&
                                props.order.Price_Group &&
                                props.catalogItem.has_price_bands &&
                                !OrderEntryService.isSumComponentPricedKit(props.catalogItem) ? (
                                    <PriceBandPriceDisplay
                                        bands={(props.catalogItem.Price_Bands || {})[props.order.Price_Group.value]}
                                        currency={props.order.Catalog?.Currency}
                                    />
                                ) : null}
                            </td>
                        </tr>
                    )}
                    <tr>
                        <td>
                            <OwLabel title={"uom"} isBold={true} noBottomMargin={true} postText={":"} />
                        </td>
                        <td>
                            {props.catalogItem && props.catalogItem.Unit_Of_Measurement ? props.catalogItem.Unit_Of_Measurement.name : null}
                        </td>
                    </tr>
                    {props.catalogItem.has_inventory && showInventoryDetails ? (
                        <React.Fragment>
                            <tr>
                                <td>
                                    <OwLabel title={"qty_on_hand"} isBold={true} noBottomMargin={true} postText={":"} />
                                </td>
                                <td>
                                    <QtyOnHandValue
                                        catalogItem={props.catalogItem}
                                        mapItem={qtyOnHandMapItem}
                                        shippingWarehouse={props.order.Shipping_Warehouse}
                                    />
                                    {props.catalogItem.Warehouse_Catalog_Items_Maps?.length ? (
                                        <div>
                                            <OwLabel title={"warehouses"} fn={viewWarehouseModal} />
                                        </div>
                                    ) : null}
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <OwLabel title={"qty_incoming"} isBold={true} noBottomMargin={true} postText={":"} />
                                </td>
                                <td>{props.catalogItem.qty_incoming}</td>
                            </tr>
                        </React.Fragment>
                    ) : null}
                    {deliveryEtaDetail}
                </tbody>
            </table>
        );
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setModalComponent(null);
    };

    const viewWarehouseModal = () => {
        const modalComp = <ItemInventoryByWarehouseModal closeModalFn={closeModal} catalogItem={props.catalogItem} />;

        setModalComponent(modalComp);
        setModalIsOpen(true);
    };

    const getAttachmentDownloadLink = (catalogItem: ICatalog_Item) => {
        if (catalogItem && catalogItem.Attachments && catalogItem.file_attachment_one_id) {
            let attachmentItem: IAttachment | null = null;

            for (const attachment of catalogItem.Attachments) {
                if (attachment.id === catalogItem.file_attachment_one_id) {
                    attachmentItem = attachment;
                }
            }

            if (attachmentItem) {
                return (
                    <div className={"d-flex"}>
                        <div className={"me-2"}>
                            <OwLabel title={"spec_file"} isBold={true} noBottomMargin={true} postText={":"} />
                        </div>
                        <OwS3DownloadLink
                            attachment={attachmentItem}
                            leftIconName={"fad fa-download"}
                            title={"download"}
                            isReadOnly={true}
                        />
                    </div>
                );
            } else {
                return null;
            }
        } else {
            return null;
        }
    };

    // Required item options must have a value before the item can be added.
    const requiredItemOptionsComplete = CatalogItemService.areRequiredItemOptionsSelected(
        props.catalogItem,
        selectedItemOptions
    ).isComplete;

    const addToCart = () => {
        if (!requiredItemOptionsComplete) {
            return;
        }

        const itemToAdd: IAddBulkItems = {
            item: {
                catalog_item_id: props.catalogItem.id,
                item_no: 0,
                qty: 0,
                Catalog_Item: props.catalogItem,
            },
            kitDetails: [],
            kitBuilderData: undefined,
            itemOptionValues: [],
        };

        if (qty) {
            itemToAdd.item.qty = props.catalogItem.has_pack_qty ? CatalogItemService.getPackItemOrderQty(props.catalogItem, qty) : qty;
        }

        if (depth) {
            itemToAdd.item.depth = depth;
        }

        if (props.supplierModel.has_discounts) {
            const priceDetail = OrderEntryService.getDiscountedPriceForCatalogItem(props.catalogItem, props.supplierModel, props.order);

            itemToAdd.item.discount_id = priceDetail.discount?.id;
        }

        if (selectedUnit) {
            // Was originally just item.uomSet = selectedUnit
            itemToAdd.item.uomSet = selectedUnit;
            itemToAdd.item.uom_set_id = selectedUnit.id;
        }

        if (selectedItemOptions.length > 0) {
            itemToAdd.itemOptionValues = selectedItemOptions.map((option) => ({
                item_option_id: option.item_option_id!,
                value: option.value,
            }));
        }

        props.addToCart(itemToAdd);
    };

    const handleSelectedUnit = (set: IUnit_Of_Measurement_Set) => {
        setSelectedUnit(set);
        setQty(1);

        // do inventory validation.
        const { enableAdd, qty: validatedQty } = OrderEntryService.doInventoryValidation(
            props.catalogItem,
            1,
            props.order.Order_Items,
            props.supplierModel,
            props.order,
            0,
            set
        );

        setEnableAdd(enableAdd);
        setQty(validatedQty);
    };

    let step = SupplierService.getStep(props.supplierModel);

    if (props.catalogItem.unit_incr_qty) {
        step = props.catalogItem.unit_incr_qty;
    }

    const uomSetQtyConversion = OrderEntryService.getUomSetQty(qty || 1, props.catalogItem?.Base_Unit, selectedUnit);

    const isNameAndDescriptionSame = useMemo(() => {
        return props.catalogItem.name?.trim() === props.catalogItem.description?.trim();
    }, [props.catalogItem.name, props.catalogItem.description]);

    return (
        <div className="OwCategoryCatalogItemDetailRow">
            <div className={"d-flex align-items-center mb-1"}>
                <ChevronToggle
                    direction={ChevronDirectionEnum.RIGHT}
                    className={""}
                    toggleFn={toggleChevron}
                    isVisible={detailVisible}
                    showChevron={true}
                />
                <div className={"ms-2 d-flex w-100 align-items-center"}>
                    <div className={"d-inline-flex me-2"}>
                        <div className={"d-flex flex-column"}>
                            <div>{props.catalogItem.name}</div>
                            <div className={`text-muted text-truncate pre-wrap ${isNameAndDescriptionSame ? "d-none" : ""}`}>
                                {(props.catalogItem.short_description?.length || 0) > 0 ? (
                                    <div className={"mb-3"}>{props.catalogItem.short_description}</div>
                                ) : null}
                                <DescriptionExpander catalogItem={props.catalogItem} />
                            </div>
                        </div>
                    </div>
                    {props.catalogItem.is_product_group && (
                        <ProductGroupSelector
                            supplierModel={props.supplierModel}
                            catalogItem={props.catalogItem}
                            order={props.order}
                            notifyFn={props.notifyFn}
                            socket={props.socket}
                            warehouses={props.warehouses}
                            addItems={props.bulkAddToCart}
                            orientation="vertical"
                        />
                    )}

                    {!props.catalogItem.is_product_group && (
                        <div className={"ms-auto w-9r d-flex flex-column align-items-center"}>
                            {!props.catalogItem.is_kit_builder ? (
                                <div className={"mb-1 w-9r"}>
                                    <OwInputWrapper
                                        name={"qty"}
                                        fn={handleChange}
                                        value={qty}
                                        type={"number"}
                                        step={step}
                                        min={props.supplierModel.allow_neg_order_qty ? undefined : 0}
                                        showStep={true}
                                        disableKeyUp={true}
                                        onWheelFn={UIService.stopScrollWheel}
                                    />
                                </div>
                            ) : null}
                            {props.supplierModel.has_uom_sets && props.catalogItem.Base_Unit ? (
                                <div className={"mt-2 mb-2"}>
                                    <UomSetSelectOption
                                        item={props.catalogItem}
                                        selectedOption={handleSelectedUnit}
                                        currentOption={selectedUnit}
                                    />
                                    {uomSetQtyConversion ? (
                                        <div className={"mt-2"}>
                                            {uomSetQtyConversion} {props.catalogItem?.Base_Unit?.unit_abbr}
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                            {props.supplierModel.show_dimension_widget && props.userModel.isSupplierUser && (
                                <div className="mt-2 mb-2">
                                    <DimensionWidget
                                        type="depth"
                                        fn={(value: number) => setDepth(value)}
                                        catalogViewer={true}
                                        compact={true}
                                        value={depth}
                                        units="imperial"
                                        buttonOnLeft={true}
                                        targetUnitId={props.catalogItem?.dimension_unit_id}
                                    />
                                </div>
                            )}
                            <div>
                                {props.catalogItem.min_order_qty ? (
                                    <div className={"text-danger text-small"}>
                                        <OwLabel title={"min_order_qty"} postText={":"} />
                                        {props.catalogItem.min_order_qty}
                                    </div>
                                ) : null}
                            </div>
                            {props.catalogItem.has_pack_qty ? (
                                <div>
                                    <PackQtyViewer catalogItem={props.catalogItem} qty={qty} />
                                </div>
                            ) : null}
                            {props.catalogItem.is_kit_builder ? (
                                <div>
                                    <OwLabel title={"please_use_main_item_lookup"} />
                                </div>
                            ) : null}
                            {props.catalogItem.Options && (
                                <div className="mt-2 mb-2">
                                    <OrderItemOptions
                                        flexDirection="flex-column"
                                        catalogItem={props.catalogItem}
                                        selectedItemOptionsFn={setSelectedItemOptionsFn}
                                    />
                                </div>
                            )}
                            {!props.catalogItem.is_kit_builder ? (
                                <OwButton
                                    disabled={!enableAdd || !requiredItemOptionsComplete}
                                    fn={addToCart}
                                    title={"add"}
                                    leftIconClass={"fas fa-plus"}
                                />
                            ) : null}
                            {/*<span*/}
                            {/*    onClick={() => {*/}
                            {/*        props.addToCart(props.catalogItem);*/}
                            {/*    }}*/}
                            {/*    className={"small-text bg-primary p-1 rounded rounded-sm pointer"}>*/}
                            {/*    <i className={"fas fa-plus pointer"} />{" "}*/}
                            {/*    <OwLabel className={"pointer"} title={"add"} noBottomMargin={true} />*/}
                            {/*</span>*/}
                            {props.catalogItem.is_ordered_on_demand ? (
                                <div className={"mt-4"}>
                                    <OwPill title={"ordered_on_demand"} className={"text-small"} />
                                </div>
                            ) : null}
                        </div>
                    )}
                </div>
            </div>
            <div className={detailVisible ? "" : "d-none"}>
                {getDetailTable()}

                {props.catalogItem.Catalog_Item_Attributes?.length ? (
                    <div>
                        <OwLabel title={"attributes"} isBold={true} className={"mt-2"} />
                        <table
                            className={
                                (UIService.isMainViewLight() ? "" : "table-dark") +
                                " table table-sm table-striped " +
                                (detailVisible ? "" : "d-none")
                            }>
                            <tbody>
                                {props.catalogItem.Catalog_Item_Attributes?.map((attribute) => {
                                    return (
                                        <tr key={attribute.id}>
                                            <th className={"w-10r"}>{attribute.Attribute?.name}</th>
                                            <td>{attribute.value}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : null}

                <FilesDownloadSection catalogItem={props.catalogItem} readOnly={true} />
            </div>
            {isOnline ? (
                <div>
                    <div className={"d-flex mb-3"}>
                        <OwS3ImageViewer
                            catalogItem={props.catalogItem}
                            isMicroView={true}
                            attachmentIndex={0}
                            className={"me-2"}
                            includeAllImages={true}
                            hideEmptyImage={true}
                        />
                    </div>
                </div>
            ) : null}
            <div>
                <Modal
                    isOpen={modalIsOpen}
                    contentLabel="Orderwerks"
                    shouldCloseOnOverlayClick={false}
                    className={"modalContentClass"}
                    overlayClassName={"modalOverlayClass"}>
                    {modalComponent}
                </Modal>
            </div>
        </div>
    );
};

export default OwCategoryCatalogItemDetailRow;
